import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';

/**
 * Workaround page to handle password reset tokens when Supabase redirects to wrong domain
 * Users can paste the full Supabase verify URL here, or we'll try to extract token from URL params
 */
const PasswordResetHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [urlInput, setUrlInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Try to extract token from current URL params
  const tokenFromUrl = searchParams.get('token');
  const typeFromUrl = searchParams.get('type');

  const handleTokenVerification = async (token, type = 'recovery') => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      console.log(`🔐 Attempting to verify token with type: ${type}`);
      
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: type
      });

      if (verifyError) {
        // Try recovery type if email failed
        if (type !== 'recovery') {
          console.log('⚠️ Email verification failed, trying recovery type...');
          const { data: recoveryData, error: recoveryError } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'recovery'
          });

          if (!recoveryError && recoveryData) {
            console.log('✅ Verified with recovery type');
            // Redirect to email confirmation page to set password
            navigate('/email-confirmation?type=recovery&token=' + token);
            return;
          }
        }

        console.error('❌ Token verification failed:', verifyError);
        setError(verifyError.message || 'Token is invalid or expired. Please request a new password reset link.');
        setLoading(false);
        return;
      }

      if (data && data.user) {
        console.log('✅ Token verified successfully, redirecting to password reset...');
        // Redirect to email confirmation page to set password
        navigate('/email-confirmation?type=recovery&token=' + token);
      } else {
        setError('Token verification succeeded but no user data returned.');
      }
    } catch (err) {
      console.error('Token verification exception:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const extractTokenFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const token = urlObj.searchParams.get('token');
      const type = urlObj.searchParams.get('type') || 'recovery';
      return { token, type };
    } catch (err) {
      // If it's not a full URL, try parsing as relative URL
      try {
        const urlObj = new URL(url, window.location.origin);
        const token = urlObj.searchParams.get('token');
        const type = urlObj.searchParams.get('type') || 'recovery';
        return { token, type };
      } catch (e) {
        return { token: null, type: null };
      }
    }
  };

  const handleUrlSubmit = (e) => {
    e?.preventDefault();
    if (!urlInput.trim()) {
      setError('Please enter a URL or token');
      return;
    }

    const { token, type } = extractTokenFromUrl(urlInput);
    if (!token) {
      // If no token found, assume the input is the token itself
      handleTokenVerification(urlInput.trim());
    } else {
      handleTokenVerification(token, type);
    }
  };

  // Auto-process token from URL if present
  React.useEffect(() => {
    if (tokenFromUrl && !loading && !success && !error) {
      console.log('🔑 Auto-processing token from URL...');
      handleTokenVerification(tokenFromUrl, typeFromUrl || 'recovery');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl, typeFromUrl]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src="/logo.svg" 
            alt="Swirl Logo" 
            className="mx-auto block h-20 w-20 mb-6"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Password Reset Helper
          </h2>
          <p className="text-gray-600">
            If the password reset link redirected you to the wrong page, paste the link here to continue.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form className="space-y-6" onSubmit={handleUrlSubmit}>
            <div>
              <label htmlFor="reset-url" className="block text-sm font-medium text-gray-700 mb-2">
                Paste Password Reset Link or Token
              </label>
              <input
                id="reset-url"
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://xlhjevmymaexbhaebkuk.supabase.co/auth/v1/verify?token=..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                disabled={loading}
              />
              <p className="mt-2 text-sm text-gray-500">
                {tokenFromUrl ? (
                  <>Auto-processing token from URL...</>
                ) : (
                  <>Copy the full URL from the email or browser address bar</>
                )}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={() => navigate('/forgot-password')}
                    className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Request a new password reset link
                  </button>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">Token verified! Redirecting...</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !urlInput.trim()}
              className="w-full flex justify-center items-center space-x-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>Continue Password Reset</span>
              )}
            </button>
          </form>

          <div className="mt-6 space-y-3">
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-500 font-medium transition-colors"
            >
              Request a new password reset link
            </button>
            <button
              onClick={() => navigate('/auth')}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-500 transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> If you landed on <code className="bg-blue-100 px-1 rounded">swirlapp.ai</code> with an error, 
            copy the full URL from your browser's address bar (including the token) and paste it above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetHandler;

